import React, { useState, useEffect } from 'react';

/**
 * 최적화된 이미지 컴포넌트
 * @param {string} filename - 이미지 파일명
 * @param {string} imagePath - 이미지 경로 (예: 'fishes', 'drink', 'preview')
 * @param {string} alt - 대체 텍스트
 * @param {string} className - CSS 클래스명
 * @param {object} style - 인라인 스타일
 * @param {function} onError - 에러 콜백
 * @param {object} placeholderStyle - 플레이스홀더 스타일
 * @param {string} loadingText - 로딩 텍스트
 * @param {string} errorText - 에러 텍스트
 */
const OptimizedImage = ({ 
    filename, 
    imagePath = 'preview',
    alt, 
    className = '', 
    style = {},
    onError,
    placeholderStyle = {},
    loadingText = 'Loading...',
    errorText = '이미지 없음'
}) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        if (filename) {
            setImageError(false);
            setImageSrc(null);
            
            const imageUrl = imagePath ? `/data/img/${imagePath}/${filename}` : `/data/img/${filename}`;
            
            // 간단한 이미지 로딩 (브라우저 캐싱 활용)
            const img = new Image();
            img.onload = () => setImageSrc(imageUrl);
            img.onerror = () => {
                setImageError(true);
                if (onError) onError();
            };
            img.src = imageUrl;
        }
    }, [filename, imagePath, onError]);

    // 기본 플레이스홀더 스타일
    const defaultPlaceholderStyle = {
        background: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
        fontSize: '12px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        minWidth: '60px',
        minHeight: '60px',
        ...placeholderStyle
    };

    const errorStyle = {
        ...defaultPlaceholderStyle,
        background: '#f8f8f8',
        color: '#ccc',
        ...placeholderStyle
    };

    if (imageError) {
        return (
            <div 
                className={`${className} image-error`} 
                style={errorStyle}
            >
                {errorText}
            </div>
        );
    }

    if (!imageSrc) {
        return (
            <div 
                className={`${className} image-placeholder`} 
                style={defaultPlaceholderStyle}
            >
                {loadingText}
            </div>
        );
    }

    return (
        <img 
            src={imageSrc}
            alt={alt}
            className={className}
            style={style}
            loading="lazy" // 브라우저 네이티브 lazy loading
            onError={() => {
                setImageError(true);
                if (onError) onError();
            }}
        />
    );
};

export default OptimizedImage;
